import ClientReportInfo from './ClientReportInfo';
import React, { useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from '@mui/material';
import { downloadReportCsv, reportMetadataRows, reportNumber, reportRate } from './clientPurchaseReportUtils';
import { reportActionSx, reportPanelSx, reportPeriodControlSx, reportTableSx } from './clientReportStyles';
import ClientReportDetails from './ClientReportDetails';

export function cohortCsvRows(response, age) {
  const cohort = response.cohorts;
  const values = (metric) => [
    metric.eligible, metric.pending_maturity, metric.excluded,
    metric.activated.count, metric.activated.rate,
    metric.second_purchase.count, metric.second_purchase.rate,
    metric.third_purchase.count, metric.third_purchase.rate,
    metric.orders, metric.orders_per_client, metric.orders_per_purchaser,
  ];
  return [
    ['Registration cohorts'], ...reportMetadataRows(response),
    ['Registration dates', response.period.dateFrom, response.period.dateTo],
    ['Days since registration', age],
    ['Denominator', 'Registered clients with the full selected observation window and valid purchase timing, including non-buyers'],
    [], ['Week from', 'Week to', 'Accounts', 'Complete observation window', 'Partial registration week', 'Eligible', 'Pending', 'Excluded', 'Activated count', 'Activated %', '2+ count', '2+ %', '3+ count', '3+ %', 'Orders', 'Orders per client', 'Orders per purchaser'],
    ...cohort.rows.map((row) => [row.dateFrom, row.dateTo, row.registrations, row.ages[age].complete, row.partial_week, ...values(row.ages[age])]),
    ['Total', '', response.summary.registrations, '', '', ...values(cohort.totals[age])],
  ];
}

function PurchaseCell({ metric }) {
  return <TableCell align="right">{reportRate(metric.rate)}<Typography variant="caption" component="div" sx={{ color: '#475569' }}>{reportNumber(metric.count)} clients</Typography></TableCell>;
}

function CohortCard({ title, registrations, metric, age, partialWeek, total = false }) {
  if (!total && registrations === 0) {
    return (
      <Box component="article" aria-label={title} sx={{ p: 1.25, borderBottom: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>No registrations</Typography>
      </Box>
    );
  }
  return (
    <Box component="article" aria-label={title} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: total ? '#eff6ff' : '#f8fafc' }}>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>{title}</Typography>
      {!total && (
        <Typography sx={{ mt: 0.25, color: metric.complete ? '#166534' : '#92400e', fontSize: '0.75rem', fontWeight: 700 }}>
          {metric.complete ? 'Complete' : 'Provisional'}{partialWeek ? ' · partial registration week' : ''}
        </Typography>
      )}
      <Typography sx={{ my: 1, color: '#475569', fontSize: '0.78rem' }}>
        {reportNumber(registrations)} accounts · {reportNumber(metric.eligible)} eligible
        {metric.pending_maturity > 0 && ` · ${reportNumber(metric.pending_maturity)} pending`}
        {metric.excluded > 0 && ` · ${reportNumber(metric.excluded)} excluded`}
      </Typography>
      <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, m: 0 }}>
        {[
          ['Activated', metric.activated], ['2+ purchases', metric.second_purchase], ['3+ purchases', metric.third_purchase],
        ].map(([label, value]) => (
          <Box key={label}>
            <Typography component="dt" sx={{ color: '#475569', fontSize: '0.72rem' }}>{label}</Typography>
            <Typography component="dd" sx={{ m: 0, fontSize: '1.15rem', fontWeight: 800, color: '#1d4ed8', fontVariantNumeric: 'tabular-nums' }}>{reportRate(value.rate)}</Typography>
            <Typography component="dd" sx={{ m: 0, fontSize: '0.72rem', color: '#475569' }}>{reportNumber(value.count)} clients</Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ mt: 1, pt: 1, borderTop: '1px solid #e2e8f0', color: '#475569', fontSize: '0.78rem' }}>
        By day {age} since registration · <Box component="strong" sx={{ color: '#0f172a' }}>{reportNumber(metric.orders_per_client, 3)}</Box> orders/client
      </Typography>
    </Box>
  );
}

export default function ClientsCohorts({ response, status }) {
  const [age, setAge] = useState(30);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!response?.cohorts) {
    return <Alert severity="info">{status === 'loading' || status === 'idle' ? 'Loading registration cohorts…' : 'Cohort data is unavailable. Refresh to retry.'}</Alert>;
  }
  const cohorts = response.cohorts;
  const renderMetric = (metric) => <>
    <TableCell align="right">
      {reportNumber(metric.eligible)}
      {metric.pending_maturity > 0 && <Typography variant="caption" component="div" sx={{ color: '#92400e' }}>{reportNumber(metric.pending_maturity)} pending</Typography>}
      {metric.excluded > 0 && <Typography variant="caption" component="div" sx={{ color: '#92400e' }}>{reportNumber(metric.excluded)} excluded</Typography>}
    </TableCell>
    <PurchaseCell metric={metric.activated} /><PurchaseCell metric={metric.second_purchase} /><PurchaseCell metric={metric.third_purchase} />
    <TableCell align="right">{reportNumber(metric.orders_per_client, 3)}</TableCell>
  </>;
  return (
    <Stack component="section" aria-label="Registration cohorts" spacing={1.5} sx={reportPanelSx}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <ToggleButtonGroup exclusive size="small" value={age} sx={reportPeriodControlSx} aria-label="Days since registration" onChange={(event, value) => { if (value) setAge(value); }}>
          {[7, 30, 60, 90].map((days) => <ToggleButton key={days} value={days}>{days} days</ToggleButton>)}
        </ToggleButtonGroup>
        {!mobile && (
          <Button size="small" variant="outlined" sx={reportActionSx} disabled={status !== 'success'} onClick={() => downloadReportCsv(cohortCsvRows(response, age), `client_cohorts_${age}d.csv`)}>Export cohorts CSV</Button>
        )}
      </Stack>
      {status === 'loading' && <LinearProgress aria-label="Refreshing registration cohorts" />}
      <Typography variant="body2" sx={{ color: '#475569' }}>
        Purchases by day {age} after registration.
        <ClientReportInfo label="About cohort figures">All rates use the eligible clients in each row. Provisional = still maturing · — = no eligible clients.</ClientReportInfo>
      </Typography>
      {response.summary.registrations === 0 ? (
        <Typography sx={{ color: '#475569', py: 2 }}>No registration weeks to compare.</Typography>
      ) : mobile ? (
        <Stack spacing={1.25} aria-label={`Client cohorts at ${age} days since registration`}>
          <CohortCard title="Total · eligible clients" total registrations={response.summary.registrations} metric={cohorts.totals[age]} age={age} />
          {cohorts.rows.map((row) => (
            <CohortCard key={row.dateFrom} title={`${row.dateFrom} – ${row.dateTo}`} registrations={row.registrations} metric={row.ages[age]} age={age} partialWeek={row.partial_week} />
          ))}
        </Stack>
      ) : <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: { md: 'block', lg: 'none' }, mb: 0.5, color: '#475569' }}>Scroll horizontally for all purchase milestones.</Typography>
        <TableContainer tabIndex={0} role="region" aria-label="Cohort comparison details" sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Table size="small" aria-label={`Client cohorts at ${age} days since registration`} sx={{ minWidth: 800, ...reportTableSx }}>
            <TableHead><TableRow>
              {['Registration week', 'Eligible', `Activated by D${age}`, `2+ by D${age}`, `3+ by D${age}`, 'Orders/client'].map((title, index) => <TableCell key={title} align={index ? 'right' : 'left'}>{title}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>
              {cohorts.rows.map((row) => {
                const metric = row.ages[age];
                if (row.registrations === 0) {
                  return <TableRow key={row.dateFrom}><TableCell>{row.dateFrom} – {row.dateTo}</TableCell><TableCell colSpan={5}>No registrations</TableCell></TableRow>;
                }
                return <TableRow key={row.dateFrom} sx={{ bgcolor: metric.complete ? undefined : '#fffbeb' }}>
                  <TableCell>{row.dateFrom} – {row.dateTo}
                  <Typography variant="caption" component="div" sx={{ color: '#475569' }}>{reportNumber(row.registrations)} accounts</Typography>
                  <Typography variant="caption" component="div" sx={{ color: '#475569' }}>
                    {metric.complete ? 'Complete' : 'Provisional'}{row.partial_week ? ' · partial registration week' : ''}
                  </Typography></TableCell>
                  {renderMetric(metric)}
                </TableRow>;
              })}
              <TableRow sx={{ '& td': { fontWeight: 750 }, bgcolor: '#f1f5f9' }}>
                <TableCell>Total · eligible clients<Typography variant="caption" component="div" sx={{ color: '#475569' }}>{reportNumber(response.summary.registrations)} accounts</Typography></TableCell>{renderMetric(cohorts.totals[age])}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>}
      <ClientReportDetails title="How to read cohorts">
        <Stack spacing={1} sx={{ textAlign: 'left' }}>
          <Typography variant="body2">Registration weeks run Monday–Sunday. Activation means the first purchase. All purchase rates and orders per client use eligible registrations, including non-buyers.</Typography>
          <Typography variant="body2">Compare completed registration weeks after the same number of days since registration. Recent weeks are provisional until every registration has the full observation window. Pending clients need more time; excluded records have invalid purchase timing. Partial registration weeks cover only the selected dates.</Typography>
          <Typography variant="body2">Available order history and current account/profile scope are used. Historical imports may be incomplete; this report is not a frozen historical snapshot.</Typography>
        </Stack>
      </ClientReportDetails>
    </Stack>
  );
}
